import { Injectable, inject } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';
import {
  FlashSaleApiService,
  FlashSaleCampaign,
  FlashSaleCampaignCreate,
  FlashSaleCampaignUpdate,
  FlashSaleAlert,
  FlashSaleScopeType,
  FlashSaleStockPreview,
  FlashSaleQuotaPolicy,
} from '@app/api/price-service/flash-sale-api.service';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { PhysicalShopControllerService } from '@app/api/channel-service/api/physicalShopController.service';
import { PhysicalShopVO } from '@app/api/channel-service/model/physicalShopVO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';

@Injectable({ providedIn: 'root' })
export class FlashSaleService {
  private readonly api = inject(FlashSaleApiService);
  private readonly goodsSku = inject(GoodsSkuControllerService);
  private readonly physicalShop = inject(PhysicalShopControllerService);

  listCampaigns(): Observable<FlashSaleCampaign[]> {
    return this.api.listCampaigns();
  }

  getCampaign(id: number): Observable<FlashSaleCampaign> {
    return this.api.getCampaign(id);
  }

  createCampaign(body: FlashSaleCampaignCreate): Observable<FlashSaleCampaign> {
    return this.api.createCampaign(body);
  }

  updateCampaign(id: number, body: FlashSaleCampaignUpdate): Observable<FlashSaleCampaign> {
    return this.api.updateCampaign(id, body);
  }

  deleteCampaign(id: number): Observable<void> {
    return this.api.deleteCampaign(id);
  }

  previewStock(
    scopeType: FlashSaleScopeType,
    physicalShopIds: number[] | undefined,
    skuCodes: string[],
  ): Observable<FlashSaleStockPreview> {
    return this.api.previewStock({ scopeType, physicalShopIds, skuCodes });
  }

  listAlerts(unreadOnly = false): Observable<FlashSaleAlert[]> {
    return this.api.listAlerts(unreadOnly);
  }

  markAlertRead(alertId: number): Observable<void> {
    return this.api.markAlertRead(alertId);
  }

  listQuotaPolicies(): Observable<FlashSaleQuotaPolicy[]> {
    return this.api.listQuotaPolicies();
  }

  createQuotaPolicy(body: {
    name: string;
    maxStockPercent: number;
    activeFlag?: boolean;
  }): Observable<FlashSaleQuotaPolicy> {
    return this.api.createQuotaPolicy(body);
  }

  updateQuotaPolicy(
    id: number,
    body: { name: string; maxStockPercent: number; activeFlag?: boolean },
  ): Observable<FlashSaleQuotaPolicy> {
    return this.api.updateQuotaPolicy(id, body);
  }

  deleteQuotaPolicy(id: number): Observable<void> {
    return this.api.deleteQuotaPolicy(id);
  }

  effectiveSkus(): Observable<{ skuCode: string; skuName: string }[]> {
    return this.goodsSku
      .getList3(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [3],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        0,
        500,
        'skuName',
        true,
      )
      .pipe(
        switchMap((payload) => from(BlobConverterUtil.convertToJson(payload))),
        map((raw) => {
          const rows = Array.isArray(raw)
            ? raw
            : ((raw as { pageContent?: { skuCode?: string; skuName?: string }[] })?.pageContent ??
              []);
          return rows
            .filter((row) => row.skuCode?.trim())
            .map((row) => ({
              skuCode: row.skuCode!.trim(),
              skuName: (row.skuName ?? row.skuCode ?? '').trim(),
            }));
        }),
      );
  }

  physicalShops(): Observable<{ id: number; name: string }[]> {
    return this.physicalShop
      .showPage({
        pageRequest: { page: 0, size: 500, sortBy: 'id', ascending: true },
      })
      .pipe(
        switchMap((payload) => from(BlobConverterUtil.convertToJson(payload))),
        map((raw) => {
          const rows = Array.isArray(raw)
            ? (raw as PhysicalShopVO[])
            : (((raw as { pageContent?: PhysicalShopVO[] })?.pageContent ?? []) as PhysicalShopVO[]);
          return rows
            .filter((row) => row.id != null)
            .map((row) => ({
              id: row.id!,
              name: (row.name ?? row.code ?? String(row.id)).trim(),
            }));
        }),
      );
  }
}
